<template>
  <div class="card shadow-sm border rounded-lg h-100">
    <div class="card-body text-center d-flex flex-column justify-content-between">
      <div>
        <h6 class="mb-1">{{ title }}</h6>
        <p class="text-xs text-muted mb-2">{{ description }}</p>
        <p class="fw-bold small text-truncate">{{ value }}</p>
      </div>

      <div class="doc-btn-row mt-3">
        <button
          v-if="canPreview"
          @click="openPreview"
          class="btn btn-primary btn-sm doc-btn"
          type="button"
        >
          <i class="fas fa-eye me-1"></i> View
        </button>

        <button
          @click="downloadFile"
          class="btn btn-success btn-sm doc-btn"
          type="button"
        >
          <i class="fas fa-download me-1"></i> Download
        </button>
      </div>
    </div>
  </div>

  <div v-if="showPreview" class="preview-overlay" @click.self="closePreview">
    <div class="preview-modal">
      <div class="preview-header">
        <h6 class="mb-0 text-truncate">{{ filename || "Document Preview" }}</h6>
        <button class="btn btn-sm btn-outline-secondary" @click="closePreview" type="button">
          Close
        </button>
      </div>

      <div class="preview-body">
        <iframe
          v-if="isPdf"
          :src="previewUrl"
          class="preview-frame"
          title="PDF Preview"
        ></iframe>

        <img
          v-else-if="isImage"
          :src="previewUrl"
          alt="Preview"
          class="preview-image"
        />

        <div v-else class="text-center p-4">
          <p>This file cannot be previewed in the browser.</p>
          <button
            @click="downloadFile"
            class="btn btn-success btn-sm"
            type="button"
          >
            <i class="fas fa-download me-1"></i> Download
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DefaultInfoCard",
  props: {
    title: String,
    description: String,
    value: [String, Number],
    content: String,
    content_type: String,
    filename: String,
    file_url: String,
  },
  data() {
    return {
      showPreview: false,
      previewUrl: null,
    };
  },
  computed: {
    isPdf() {
      return (this.content_type || "").toLowerCase().includes("pdf");
    },
    isImage() {
      const type = (this.content_type || "").toLowerCase();
      return type.includes("image/");
    },
    canPreview() {
      return this.isPdf || this.isImage;
    },
    hasDirectUrl() {
      return !!this.file_url;
    },
  },
  methods: {
    buildBlob() {
      if (!this.content) return null;

      const byteCharacters = atob(this.content);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: this.content_type });
    },

    downloadFile() {
      try {
        if (this.hasDirectUrl) {
          const link = document.createElement("a");
          link.href = this.file_url;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.download = this.filename || "document";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }

        const blob = this.buildBlob();
        if (!blob) return;

        const link = document.createElement("a");
        const objectUrl = URL.createObjectURL(blob);

        link.href = objectUrl;
        link.download = this.filename || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(objectUrl);
      } catch (e) {
        console.error("Download failed:", e);
      }
    },

    openPreview() {
      try {
        if (!this.canPreview) {
          this.downloadFile();
          return;
        }

        if (this.hasDirectUrl) {
          this.previewUrl = this.file_url;
          this.showPreview = true;
          return;
        }

        const blob = this.buildBlob();
        if (!blob) return;

        this.previewUrl = URL.createObjectURL(blob);
        this.showPreview = true;
      } catch (e) {
        console.error("Preview failed:", e);
      }
    },

    closePreview() {
      this.showPreview = false;

      if (this.previewUrl && this.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.previewUrl);
      }

      this.previewUrl = null;
    },
  },
  beforeUnmount() {
    if (this.previewUrl && this.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.previewUrl);
    }
  },
};
</script>

<style scoped>
.doc-btn-row {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
}

.doc-btn {
  min-width: 100px;
}

.preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.preview-modal {
  background: #fff;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.preview-body {
  padding: 1rem;
  max-height: calc(90vh - 60px);
  overflow: auto;
}

.preview-frame {
  width: 100%;
  height: 70vh;
  border: 0;
}

.preview-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
}

@media (max-width: 768px) {
  .doc-btn-row {
    flex-direction: column;
  }

  .doc-btn {
    width: 100%;
  }

  .preview-frame {
    height: 60vh;
  }
}
</style>
